import { useEffect, useMemo, useRef, useState } from "react";
import api, { fmtARS, fmtDate } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  MagnifyingGlass, PencilSimple, Trash, Package, DownloadSimple, UploadSimple,
  Plus, ListNumbers, FileArrowDown, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { RecetaCostoEditor, normalizarReceta } from "@/components/RecetaCostoEditor";
import NumberInput from "@/components/ui/NumberInput";

export default function ProductosPage() {
  const IVA_OPCIONES = [
    { value: "exento", numeric: 0, label: "Exento" },
    { value: "0", numeric: 0, label: "0%" },
    { value: "10.5", numeric: 10.5, label: "10,5%" },
    { value: "21", numeric: 21, label: "21%" },
    { value: "27", numeric: 27, label: "27%" },
  ];
  const [tab, setTab] = useState("catalogo");
  const [productos, setProductos] = useState([]);
  const [listas, setListas] = useState([]);
  const [batches, setBatches] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", unidad: "kg", iva_pct: 10.5, codigo_afip: "", ultimo_costo: 0, precios: {} });
  const [delTarget, setDelTarget] = useState(null);
  const [newListaOpen, setNewListaOpen] = useState(false);
  const [newListaName, setNewListaName] = useState("");
  const [editListaId, setEditListaId] = useState(null);
  const [editListaName, setEditListaName] = useState("");
  const [delLista, setDelLista] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const [newProdOpen, setNewProdOpen] = useState(false);
  const [newProd, setNewProd] = useState({ codigo: "", nombre: "", unidad: "kg", iva_pct: 10.5, ultimo_costo: "", precio_minorista: "", precio_mayorista: "" });
  const [creatingProd, setCreatingProd] = useState(false);
  const [afipCatalog, setAfipCatalog] = useState([]);
  const [afipSearch, setAfipSearch] = useState("");
  const [rubros, setRubros] = useState([]);
  const [newRubroOpen, setNewRubroOpen] = useState(false);
  const [rubroForm, setRubroForm] = useState({ nombre: "", color: "#921A0E", orden: 0 });
  const [editRubroId, setEditRubroId] = useState(null);

  const load = async () => {
    const [{ data: prod }, { data: ls }, { data: rb }] = await Promise.all([
      api.get("/productos"),
      api.get("/listas-precios"),
      api.get("/rubros"),
    ]);
    setProductos(prod);
    setListas(ls.sort((a, b) => (a.orden || 0) - (b.orden || 0)));
    setRubros(rb || []);
  };
  const loadBatches = async () => {
    const { data } = await api.get("/import-batches");
    setBatches((data || []).filter((b) => b.type === "precios_productos" || b.type === "productos_simple"));
  };
  const loadAfipCatalog = async () => {
    // Cargar catálogo AFIP/SENASA para el selector de código AFIP
    // (bovino + porcino, cacheado en backend).
    if (afipCatalog.length > 0) return;
    try {
      const [{ data: bov }, { data: por }] = await Promise.all([
        api.get("/rec/catalog/tipos-por-especie?especie=bovino"),
        api.get("/rec/catalog/tipos-por-especie?especie=porcino"),
      ]);
      const all = [
        ...(bov || []).map((t) => ({ ...t, especie: "Bovino" })),
        ...(por || []).map((t) => ({ ...t, especie: "Porcino" })),
      ];
      setAfipCatalog(all);
    } catch (e) {
      // silencioso: si falla no bloqueamos la edición, el input queda libre
    }
  };

  useEffect(() => { load(); loadBatches(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      nombre: p.nombre,
      unidad: p.unidad || "kg",
      iva_pct: p.iva_pct != null ? p.iva_pct : 10.5,
      codigo_afip: p.codigo_afip || "",
      rubro_id: p.rubro_id || null,
      ultimo_costo: p.ultimo_costo != null ? p.ultimo_costo : 0,
      receta_costo: (p.receta_costo || []).map((r) => ({ ...r })),
      costo_por_receta: !!p.costo_por_receta,
      stock_infinito_tn: !!p.stock_infinito_tn,
      precios: { ...(p.precios || {}) },
    });
    setAfipSearch("");
    loadAfipCatalog();
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const baseId = listas[0]?.id;
      const baseVal = baseId ? parseFloat(form.precios[baseId]) : undefined;
      const preciosClean = {};
      Object.entries(form.precios).forEach(([lid, v]) => {
        const num = parseFloat(v);
        if (!isNaN(num)) preciosClean[lid] = num;
      });
      await api.put(`/productos/${editing.id}`, {
        nombre: form.nombre,
        unidad: form.unidad,
        iva_pct: parseFloat(form.iva_pct) || 0,
        codigo_afip: form.codigo_afip?.trim() || null,
        rubro_id: form.rubro_id || null,
        precios: preciosClean,
        ultimo_costo: !isNaN(parseFloat(form.ultimo_costo)) ? parseFloat(form.ultimo_costo) : 0,
        stock_infinito_tn: !!form.stock_infinito_tn,
        ...normalizarReceta(form),
        ...(baseVal != null && !isNaN(baseVal) ? { precio_actual: baseVal } : {}),
      });
      toast.success("Producto actualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/productos/${delTarget.id}`);
      toast.success("Producto eliminado");
      setDelTarget(null);
      load();
    } catch {
      toast.error("Error");
    }
  };

  // Listas CRUD
  const createLista = async () => {
    const nombre = newListaName.trim();
    if (!nombre) return toast.error("Ingresá un nombre");
    try {
      await api.post("/listas-precios", { nombre });
      toast.success(`Lista "${nombre}" creada`);
      setNewListaOpen(false);
      setNewListaName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };
  const saveListaName = async () => {
    if (!editListaId) return;
    try {
      await api.put(`/listas-precios/${editListaId}`, { nombre: editListaName.trim() });
      toast.success("Lista actualizada");
      setEditListaId(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "Error de validación") : "Error";
      toast.error(msg);
    }
  };
  // Marca/desmarca una lista como fuente de precios para Tienda Nube.
  // Es mutuamente exclusivo (backend apaga el flag en las demás automático).
  const toggleListaTn = async (l) => {
    const nuevoValor = !l.es_lista_tn;
    if (nuevoValor && !window.confirm(
      `¿Marcar "${l.nombre}" como la lista de precios que se va a sincronizar con Tienda Nube?\n\n` +
      `Esto reemplaza a cualquier otra lista que estuviera marcada.`
    )) return;
    try {
      await api.put(`/listas-precios/${l.id}`, { es_lista_tn: nuevoValor });
      toast.success(nuevoValor ? `"${l.nombre}" es ahora la lista TN` : "Desmarcada como lista TN");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "Error de validación") : "Error";
      toast.error(msg);
    }
  };

  // Marca/desmarca una lista como la predeterminada del POS (Mostrador).
  const toggleListaPos = async (l) => {
    const nuevoValor = !l.es_lista_pos;
    if (nuevoValor && !window.confirm(
      `¿Marcar "${l.nombre}" como la lista de precios por defecto para el POS (Mostrador)?\n\n` +
      `Al abrir el punto de venta siempre se cobrará con esta lista.`
    )) return;
    try {
      await api.put(`/listas-precios/${l.id}`, { es_lista_pos: nuevoValor });
      toast.success(nuevoValor ? `"${l.nombre}" es ahora la lista del POS (Mostrador)` : "Desmarcada como lista POS");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "Error de validación") : "Error";
      toast.error(msg);
    }
  };
  const confirmDeleteLista = async () => {
    if (!delLista) return;
    try {
      await api.delete(`/listas-precios/${delLista.id}`);
      toast.success("Lista eliminada");
      setDelLista(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  // Export
  const exportExcel = async () => {
    try {
      const res = await api.get("/productos/export-precios", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `listas_precios_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportación descargada");
    } catch (err) {
      toast.error("Error al exportar");
    }
  };

  // Import (dinámico: A=Código, B=Nombre, resto de columnas leen su header para matchear con Listas de Precios)
  const importExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/productos/import-simple", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.duplicate) {
        toast.info(data.message);
      } else {
        const listasMsg = (data.listas_detectadas || [])
          .map((ld) => `${ld.columna}=${ld.lista}`)
          .join(" · ");
        toast.success(
          `Importados: ${data.productos_actualizados} actualizados, ${data.productos_creados} nuevos` +
          ((data.costos_actualizados || 0) > 0 ? ` · ${data.costos_actualizados} costos actualizados` : "") +
          (listasMsg ? ` · Columnas: ${listasMsg}` : "")
        );
        if ((data.costos_omitidos_receta || 0) > 0) {
          toast.info(`${data.costos_omitidos_receta} costo(s) no se tocaron porque se calculan por receta`, { duration: 8000 });
        }
        if ((data.headers_ignorados || []).length > 0) {
          toast.warning(
            `Headers ignorados (no coinciden con listas): ${data.headers_ignorados.join(", ")}`,
            { duration: 8000 }
          );
        }
      }
      setImportResult(data);
      load();
      loadBatches();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Nuevo producto manual (con auto-código sugerido)
  const openNewProd = async () => {
    try {
      const { data } = await api.get("/productos/next-codigo");
      setNewProd({
        codigo: data?.codigo || "",
        nombre: "",
        unidad: "kg",
        iva_pct: 10.5,
        ultimo_costo: "",
        precio_minorista: "",
        precio_mayorista: "",
      });
      setNewProdOpen(true);
    } catch {
      toast.error("No se pudo obtener el próximo código");
    }
  };

  const createProdManual = async () => {
    const nombre = (newProd.nombre || "").trim();
    if (!nombre) return toast.error("Ingresá el nombre del producto");
    const codigo = (newProd.codigo || "").trim();
    if (!codigo) return toast.error("Código requerido");
    const dupe = productos.find((p) => (p.codigo || "").toLowerCase() === codigo.toLowerCase());
    if (dupe) return toast.error(`El código "${codigo}" ya está en uso por "${dupe.nombre}"`);
    setCreatingProd(true);
    try {
      const pmin = parseFloat(newProd.precio_minorista);
      const pcosto = parseFloat(newProd.ultimo_costo);
      const created = await api.post("/productos", {
        codigo,
        nombre,
        unidad: newProd.unidad || "kg",
        iva_pct: parseFloat(newProd.iva_pct) || 0,
        precio_actual: !isNaN(pmin) ? pmin : 0,
        rubro_id: newProd.rubro_id || null,
        ultimo_costo: !isNaN(pcosto) ? pcosto : 0,
      });
      // Asignar precios en listas (base = minorista, buscar/crear "Mayorista" si hace falta)
      const preciosMap = {};
      if (!isNaN(pmin) && listas[0]?.id) preciosMap[listas[0].id] = pmin;
      const pmay = parseFloat(newProd.precio_mayorista);
      if (!isNaN(pmay)) {
        let mayLista = listas.find((l) => (l.nombre || "").toLowerCase() === "mayorista");
        if (!mayLista) {
          const { data: nm } = await api.post("/listas-precios", { nombre: "Mayorista" });
          mayLista = nm;
        }
        preciosMap[mayLista.id] = pmay;
      }
      if (Object.keys(preciosMap).length > 0 && created?.data?.id) {
        await api.put(`/productos/${created.data.id}`, { precios: preciosMap });
      }
      toast.success(`Producto "${nombre}" creado (código ${codigo})`);
      setNewProdOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear producto");
    } finally {
      setCreatingProd(false);
    }
  };

  const deleteBatch = async (batch) => {
    if (!window.confirm(`¿Eliminar el lote "${batch.filename}"? Se conservan los cambios ya aplicados.`)) return;
    try {
      await api.delete(`/import-batches/${batch.id}`);
      toast.success("Lote eliminado del historial");
      loadBatches();
    } catch (err) {
      toast.error("Error");
    }
  };

  const saveRubro = async () => {
    const nombre = (rubroForm.nombre || "").trim();
    if (!nombre) return toast.error("Nombre del rubro requerido");
    try {
      let createdId = null;
      if (editRubroId) {
        await api.put(`/rubros/${editRubroId}`, rubroForm);
        toast.success("Rubro actualizado");
      } else {
        const { data } = await api.post("/rubros", rubroForm);
        createdId = data?.id || null;
        toast.success("Rubro creado");
      }
      setNewRubroOpen(false);
      setEditRubroId(null);
      setRubroForm({ nombre: "", color: "#921A0E", orden: 0 });
      // Si estábamos creando desde el form de producto, auto-selecciónalo
      if (createdId && open) {
        setForm((f) => ({ ...f, rubro_id: createdId }));
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar rubro");
    }
  };

  const deleteRubro = async (r) => {
    if (!window.confirm(`¿Eliminar el rubro "${r.nombre}"? Los productos asociados quedarán sin rubro.`)) return;
    try {
      await api.delete(`/rubros/${r.id}`);
      toast.success("Rubro eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar rubro");
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return productos;
    const s = q.toLowerCase();
    return productos.filter(
      (p) => p.nombre.toLowerCase().includes(s) || (p.codigo || "").toLowerCase().includes(s)
    );
  }, [productos, q]);

  return (
    <>
      <Topbar
        title="Productos y precios"
        subtitle="Catálogo, listas de precios (mostrador, mayorista, distribuidor…) e importaciones."
        right={
          <div className="flex gap-2 items-center">
            <Button
              size="sm" className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]"
              onClick={openNewProd}
              data-testid="btn-new-producto"
            >
              <Plus size={14} className="mr-1" /> Nuevo producto
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={exportExcel} data-testid="btn-export-precios">
              <DownloadSimple size={14} className="mr-2" /> Exportar Excel
            </Button>
            <input
              ref={fileRef} type="file" accept=".xlsx,.xls"
              className="hidden"
              data-testid="import-precios-file"
              onChange={(e) => e.target.files?.[0] && importExcel(e.target.files[0])}
            />
            <Button
              variant="outline" size="sm" className="rounded-sm h-9"
              onClick={() => fileRef.current?.click()}
              data-testid="btn-import-precios"
              disabled={importing}
              title="Formato: Col A=Código · Col B=Nombre · Col C en adelante = precios (usar como header el nombre exacto de la lista, ej: Minorista, Mayorista)"
            >
              <UploadSimple size={14} className="mr-2" /> {importing ? "Importando…" : "Importar Excel"}
            </Button>
          </div>
        }
      />
      <div className="p-4 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm h-9">
            <TabsTrigger value="catalogo" className="rounded-sm text-[12px]" data-testid="tab-catalogo">
              Catálogo · {productos.length}
            </TabsTrigger>
            <TabsTrigger value="listas" className="rounded-sm text-[12px]" data-testid="tab-listas">
              Listas de precios · {listas.length}
            </TabsTrigger>
            <TabsTrigger value="historial" className="rounded-sm text-[12px]" data-testid="tab-historial">
              Historial · {batches.length}
            </TabsTrigger>
            <TabsTrigger value="rubros" className="rounded-sm text-[12px]" data-testid="tab-rubros">
              Rubros · {rubros.length}
            </TabsTrigger>
          </TabsList>

          {/* CATALOGO */}
          <TabsContent value="catalogo" className="mt-4">
            <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
              <div className="px-5 py-3 border-b bg-neutral-50 flex items-center gap-2">
                <Package size={16} weight="bold" className="text-[#6B1F24]" />
                <h2 className="font-heading font-semibold text-[15px]">Catálogo</h2>
                <div className="ml-auto flex items-center gap-2">
                  <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#999]" />
                    <Input
                      data-testid="productos-search"
                      placeholder="Buscar código o nombre…"
                      className="rounded-sm h-8 w-64 pl-8 text-[12px]"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                  <span className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                    {filtered.length}
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px] grid-table">
                  <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                    <tr>
                      <th className="px-4 py-2 text-left w-20">Código</th>
                      <th className="px-4 py-2 text-left min-w-[200px]">Nombre</th>
                      <th className="px-4 py-2 text-left w-16">Un.</th>
                      <th className="px-4 py-2 text-left w-28">Rubro</th>
                      <th className="px-4 py-2 text-right w-16">IVA%</th>
                      <th className="px-4 py-2 text-left w-24 text-[#6B1F24]" title="Código AFIP / SENASA (REC)">AFIP</th>
                      <th className="px-4 py-2 text-right w-28 text-[#6B1F24]" title="Costo unitario del producto (se actualiza con cada compra si está tildado)">Costo</th>
                      <th className="px-4 py-2 text-center w-14 text-[#6B1F24]" title="Stock infinito en Tienda Nube: al sincronizar stock se envía 9999">TN∞</th>
                      {listas.map((l) => (
                        <th key={l.id} className="px-3 py-2 text-right w-32 text-[#6B1F24]">
                          {l.nombre}
                        </th>
                      ))}
                      <th className="px-4 py-2 text-right w-20">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id} data-testid={`prod-${p.id}`} className="group hover:bg-neutral-50">
                        <td className="px-4 py-2 font-mono text-[#6B1F24] text-[12px]">{p.codigo}</td>
                        <td className="px-4 py-2 font-medium">{p.nombre}</td>
                        <td className="px-4 py-2 text-[#666] text-[11px] uppercase font-mono">{p.unidad}</td>
                        <td className="px-4 py-2 text-[11px]">
                          {(() => {
                            const r = rubros.find(rr => rr.id === p.rubro_id);
                            return r ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
                                {r.nombre}
                              </span>
                            ) : <span className="text-neutral-300">—</span>;
                          })()}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-[12px] text-[#666]">
                          {p.iva_pct != null ? `${(p.iva_pct % 1 === 0 ? p.iva_pct.toFixed(0) : p.iva_pct.toFixed(1)).replace('.', ',')}%` : "—"}
                        </td>
                        <td className="px-4 py-2 font-mono text-[11px] text-[#6B1F24]">
                          {p.codigo_afip || <span className="text-neutral-300">—</span>}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-[12px]" data-testid={`prod-costo-${p.id}`}>
                          {p.ultimo_costo != null && p.ultimo_costo !== 0
                            ? <span className="text-[#333]">{fmtARS(p.ultimo_costo)}</span>
                            : <span className="text-[#ccc]">—</span>}
                        </td>
                        <td className="px-2 py-2 text-center" data-testid={`prod-tn-inf-cell-${p.id}`}>
                          <input
                            type="checkbox"
                            className="accent-[#6B1F24] cursor-pointer"
                            checked={!!p.stock_infinito_tn}
                            onChange={async (e) => {
                              const value = e.target.checked;
                              try {
                                await api.put(`/productos/${p.id}`, { stock_infinito_tn: value });
                                setProductos((ps) => ps.map((x) => (x.id === p.id ? { ...x, stock_infinito_tn: value } : x)));
                                toast.success(`${p.nombre} · Stock infinito TN ${value ? "activado" : "desactivado"}`);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Error al guardar");
                              }
                            }}
                            title={`Stock infinito Tienda Nube · ${p.stock_infinito_tn ? "ACTIVO" : "inactivo"}`}
                            data-testid={`prod-tn-inf-${p.id}`}
                          />
                        </td>
                        {listas.map((l) => {
                          const val = (p.precios && p.precios[l.id] != null)
                            ? p.precios[l.id]
                            : (l === listas[0] ? p.precio_actual : null);
                          return (
                            <td key={l.id} className="px-3 py-2 text-right font-mono">
                              {val != null && val !== 0 ? fmtARS(val) :
                                <span className="text-[#ccc]">—</span>}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100">
                            <button
                              data-testid={`edit-prod-${p.id}`}
                              onClick={() => openEdit(p)}
                              className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666]"
                            >
                              <PencilSimple size={13} />
                            </button>
                            <button
                              data-testid={`del-prod-${p.id}`}
                              onClick={() => setDelTarget(p)}
                              className="p-1.5 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                            >
                              <Trash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={8 + listas.length} className="text-center py-8 text-[#999]">
                          Sin productos.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* LISTAS */}
          <TabsContent value="listas" className="mt-4">
            <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
              <div className="px-5 py-3 border-b bg-neutral-50 flex items-center gap-2">
                <ListNumbers size={16} weight="bold" className="text-[#6B1F24]" />
                <h2 className="font-heading font-semibold text-[15px]">Listas de precios</h2>
                <Button
                  size="sm" className="ml-auto rounded-sm h-8 bg-[#6B1F24] hover:bg-[#571519]"
                  onClick={() => { setNewListaName(""); setNewListaOpen(true); }}
                  data-testid="btn-new-lista"
                >
                  <Plus size={12} className="mr-1" /> Nueva lista
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                    <tr>
                      <th className="px-5 py-2 text-left w-16">#</th>
                      <th className="px-5 py-2 text-left">Nombre</th>
                      <th className="px-5 py-2 text-right">Precios cargados</th>
                      <th className="px-5 py-2 text-left w-40">Creada</th>
                      <th className="px-5 py-2 text-right w-32">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listas.map((l, i) => {
                      const conPrecio = productos.filter((p) => p.precios && p.precios[l.id] != null && p.precios[l.id] !== 0).length;
                      const isEditing = editListaId === l.id;
                      return (
                        <tr key={l.id} data-testid={`lista-${l.id}`} className="group hover:bg-neutral-50 border-t">
                          <td className="px-5 py-2 font-mono text-[#666]">{i + 1}</td>
                          <td className="px-5 py-2 font-medium">
                            {isEditing ? (
                              <div className="flex gap-2">
                                <Input
                                  autoFocus
                                  data-testid={`edit-lista-input-${l.id}`}
                                  className="rounded-sm h-7 text-[12px]"
                                  value={editListaName}
                                  onChange={(e) => setEditListaName(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && saveListaName()}
                                />
                                <Button size="sm" className="rounded-sm h-7 bg-[#6B1F24]" onClick={saveListaName}
                                  data-testid={`save-lista-${l.id}`}>Guardar</Button>
                                <Button size="sm" variant="outline" className="rounded-sm h-7"
                                  onClick={() => setEditListaId(null)}>Cancelar</Button>
                              </div>
                            ) : (
                              <div className="inline-flex items-center gap-2">
                                <span className="text-[#6B1F24] font-mono">{l.nombre}</span>
                                {l.es_lista_pos && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300"
                                    title="Esta lista se usa por defecto al vender en el POS (Mostrador)"
                                    data-testid={`lista-pos-badge-${l.id}`}
                                  >
                                    POS Mostrador
                                  </span>
                                )}
                                {l.es_lista_tn && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-sky-100 text-sky-800 border border-sky-300"
                                    title="Los precios de esta lista se sincronizan con Tienda Nube"
                                    data-testid={`lista-tn-badge-${l.id}`}
                                  >
                                    Tienda Nube
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-2 text-right font-mono text-[12px]">
                            {conPrecio} / {productos.length}
                          </td>
                          <td className="px-5 py-2 font-mono text-[12px] text-[#666]">{fmtDate(l.created_at)}</td>
                          <td className="px-5 py-2 text-right">
                            <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100">
                              <button
                                data-testid={`toggle-lista-pos-${l.id}`}
                                onClick={() => toggleListaPos(l)}
                                className={`px-2 py-1 rounded-sm text-[10px] font-bold uppercase ${
                                  l.es_lista_pos
                                    ? "bg-emerald-700 text-white hover:bg-emerald-800"
                                    : "bg-neutral-100 text-neutral-600 hover:bg-emerald-100 hover:text-emerald-800"
                                }`}
                                title={l.es_lista_pos ? "Desmarcar como lista por defecto del POS" : "Marcar como lista por defecto del POS (Mostrador)"}
                              >
                                {l.es_lista_pos ? "POS ✓" : "POS"}
                              </button>
                              <button
                                data-testid={`toggle-lista-tn-${l.id}`}
                                onClick={() => toggleListaTn(l)}
                                className={`px-2 py-1 rounded-sm text-[10px] font-bold uppercase ${
                                  l.es_lista_tn
                                    ? "bg-sky-600 text-white hover:bg-sky-700"
                                    : "bg-neutral-100 text-neutral-600 hover:bg-sky-100 hover:text-sky-800"
                                }`}
                                title={l.es_lista_tn ? "Desmarcar como lista TN" : "Marcar como lista para sincronizar con Tienda Nube"}
                              >
                                {l.es_lista_tn ? "TN ✓" : "TN"}
                              </button>
                              <button
                                data-testid={`edit-lista-${l.id}`}
                                onClick={() => { setEditListaId(l.id); setEditListaName(l.nombre); }}
                                className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666]"
                              >
                                <PencilSimple size={13} />
                              </button>
                              <button
                                data-testid={`del-lista-${l.id}`}
                                onClick={() => setDelLista(l)}
                                className="p-1.5 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                                disabled={listas.length <= 1}
                                title={listas.length <= 1 ? "No se puede eliminar la única lista" : ""}
                              >
                                <Trash size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* HISTORIAL */}
          <TabsContent value="historial" className="mt-4">
            <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
              <div className="px-5 py-3 border-b bg-neutral-50 flex items-center gap-2">
                <FileArrowDown size={16} weight="bold" className="text-[#6B1F24]" />
                <h2 className="font-heading font-semibold text-[15px]">Historial de importaciones</h2>
                <span className="ml-auto text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  {batches.length} lotes
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                    <tr>
                      <th className="px-5 py-2 text-left">Archivo</th>
                      <th className="px-5 py-2 text-left w-40">Fecha</th>
                      <th className="px-5 py-2 text-right w-24">Actualizados</th>
                      <th className="px-5 py-2 text-right w-20">Nuevos</th>
                      <th className="px-5 py-2 text-left w-40">Listas creadas</th>
                      <th className="px-5 py-2 text-right w-20">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.id} data-testid={`batch-${b.id}`} className="border-t hover:bg-neutral-50 group">
                        <td className="px-5 py-2 font-mono text-[12px]">{b.filename}</td>
                        <td className="px-5 py-2 font-mono text-[12px] text-[#666]">{fmtDate(b.created_at)}</td>
                        <td className="px-5 py-2 text-right font-mono">{b.productos_actualizados ?? 0}</td>
                        <td className="px-5 py-2 text-right font-mono">{b.productos_creados ?? 0}</td>
                        <td className="px-5 py-2 text-[11px] text-[#666]">
                          {(b.listas_creadas || []).length > 0 ? b.listas_creadas.join(", ") : "—"}
                        </td>
                        <td className="px-5 py-2 text-right">
                          <button
                            data-testid={`del-batch-${b.id}`}
                            onClick={() => deleteBatch(b)}
                            className="p-1.5 opacity-60 group-hover:opacity-100 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                          >
                            <Trash size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {batches.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-[#999]">
                          Sin importaciones de precios aún.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* RUBROS */}
          <TabsContent value="rubros" className="mt-4">
            <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
              <div className="px-5 py-3 border-b bg-neutral-50 flex items-center gap-2">
                <Package size={16} weight="bold" className="text-[#6B1F24]" />
                <h2 className="font-heading font-semibold text-[15px]">Rubros del POS</h2>
                <span className="text-[11px] text-neutral-500 ml-2">Usados para agrupar productos en la pantalla Punto de Venta</span>
                <div className="ml-auto">
                  <Button
                    data-testid="btn-nuevo-rubro"
                    size="sm"
                    onClick={() => {
                      setEditRubroId(null);
                      setRubroForm({ nombre: "", color: "#921A0E", orden: (rubros.length || 0) + 1 });
                      setNewRubroOpen(true);
                    }}
                    className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] text-[12px]"
                  >
                    <Plus size={14} className="mr-1" /> Nuevo rubro
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                    <tr>
                      <th className="px-5 py-2 text-left w-16">Orden</th>
                      <th className="px-5 py-2 text-left w-20">Color</th>
                      <th className="px-5 py-2 text-left">Nombre</th>
                      <th className="px-5 py-2 text-right w-32">Productos</th>
                      <th className="px-5 py-2 text-right w-40">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rubros.map((r) => {
                      const count = productos.filter((p) => p.rubro_id === r.id).length;
                      return (
                        <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`rubro-row-${r.id}`}>
                          <td className="px-5 py-2 font-mono text-[#666]">{r.orden ?? 0}</td>
                          <td className="px-5 py-2">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ backgroundColor: r.color }}>
                              {r.nombre.substring(0, 3).toUpperCase()}
                            </div>
                          </td>
                          <td className="px-5 py-2 font-medium">{r.nombre}</td>
                          <td className="px-5 py-2 text-right font-mono text-[12px]">{count}</td>
                          <td className="px-5 py-2 text-right">
                            <div className="inline-flex gap-1">
                              <Button
                                size="sm" variant="outline"
                                className="rounded-sm h-7 text-[11px]"
                                onClick={() => {
                                  setEditRubroId(r.id);
                                  setRubroForm({ nombre: r.nombre, color: r.color, orden: r.orden || 0, activo: r.activo });
                                  setNewRubroOpen(true);
                                }}
                                data-testid={`rubro-edit-${r.id}`}
                              >
                                Editar
                              </Button>
                              <Button
                                size="sm" variant="outline"
                                className="rounded-sm h-7 text-[11px] text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => deleteRubro(r)}
                                data-testid={`rubro-delete-${r.id}`}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {rubros.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-[#999]">
                          Sin rubros. Creá al menos uno para agrupar productos en el Punto de Venta.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Editar producto */}
      <Sheet open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <SheetContent className="w-full sm:w-[520px] sm:max-w-[520px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Editar producto</SheetTitle>
          </SheetHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4 mt-6">
              <div className="text-[11px] font-mono text-[#666]">
                Código: <span className="text-[#6B1F24]">{editing.codigo}</span> · Editá el nombre, la unidad y los precios de cada lista.
              </div>
              <div>
                <Label className="text-[12px]">Nombre</Label>
                <Input
                  data-testid="prod-nombre" required className="rounded-sm mt-1"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px]">Unidad</Label>
                  <Input
                    className="rounded-sm mt-1 font-mono"
                    value={form.unidad}
                    onChange={(e) => setForm({ ...form, unidad: e.target.value })}
                    placeholder="kg / u / lt"
                  />
                </div>
                <div>
                  <Label className="text-[12px]">Alícuota IVA</Label>
                  <Select
                    value={String(form.iva_pct)}
                    onValueChange={(v) => setForm({ ...form, iva_pct: parseFloat(v) })}
                  >
                    <SelectTrigger data-testid="prod-iva" className="rounded-sm mt-1 font-mono">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {IVA_OPCIONES.map((o) => (
                        <SelectItem key={o.value} value={String(o.numeric)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[12px]">Rubro <span className="text-neutral-400 text-[10px]">(agrupación POS)</span></Label>
                  <button
                    type="button"
                    onClick={() => {
                      setEditRubroId(null);
                      setRubroForm({ nombre: "", color: "#921A0E", orden: rubros.length });
                      setNewRubroOpen(true);
                    }}
                    className="text-[10px] text-emerald-700 hover:text-emerald-900 hover:underline font-mono"
                    data-testid="prod-add-rubro"
                  >
                    + Nuevo rubro
                  </button>
                </div>
                <Select
                  value={form.rubro_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, rubro_id: v === "__none__" ? null : v })}
                >
                  <SelectTrigger data-testid="prod-rubro" className="rounded-sm mt-1">
                    <SelectValue placeholder="Sin rubro" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sin rubro —</SelectItem>
                    {rubros.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                          {r.nombre}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Código AFIP / SENASA <span className="text-neutral-400 text-[10px] font-normal">(REC Cárnico)</span></Label>
                <div className="border border-neutral-200 rounded-sm mt-1 bg-white">
                  {/* Chip del valor actual */}
                  {form.codigo_afip && (
                    <div className="flex items-center gap-2 border-b px-2 py-1 bg-emerald-50/40">
                      <span className="text-[11px] font-mono font-semibold text-emerald-800">
                        {form.codigo_afip}
                      </span>
                      <span className="text-[11px] text-neutral-600 flex-1 truncate">
                        {(afipCatalog.find((o) => String(o.code) === String(form.codigo_afip))?.label) || "Código libre"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, codigo_afip: "" })}
                        className="text-[10px] text-red-600 hover:underline"
                      >
                        Quitar
                      </button>
                    </div>
                  )}
                  {/* Buscador */}
                  <input
                    type="text"
                    className="w-full px-2 py-1.5 text-xs outline-none border-b"
                    placeholder={afipCatalog.length ? "Buscar por código o nombre… (ej: 1.1, media res)" : "Cargando catálogo AFIP…"}
                    value={afipSearch}
                    onChange={(e) => setAfipSearch(e.target.value)}
                    data-testid="prod-codigo-afip-search"
                  />
                  {/* Lista */}
                  <div className="max-h-56 overflow-y-auto text-xs" data-testid="prod-codigo-afip-list">
                    {(() => {
                      const s = afipSearch.trim().toLowerCase();
                      const filtered = afipCatalog.filter(
                        (o) => !s || String(o.code).toLowerCase().includes(s) || (o.label || "").toLowerCase().includes(s)
                      );
                      if (filtered.length === 0) {
                        return (
                          <div className="p-2 text-neutral-400">
                            {afipCatalog.length ? "Sin coincidencias" : "Sin catálogo disponible. Podés escribir el código manualmente arriba."}
                          </div>
                        );
                      }
                      // Agrupar por especie
                      const byEspecie = filtered.reduce((acc, o) => {
                        (acc[o.especie || o.grupo_label || "Otros"] ||= []).push(o);
                        return acc;
                      }, {});
                      return Object.entries(byEspecie).map(([esp, opts]) => (
                        <div key={esp}>
                          <div className="px-2 py-1 bg-neutral-100 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold sticky top-0">
                            {esp}
                          </div>
                          {opts.map((o) => (
                            <button
                              key={`${o.code}-${o.grupo_code || ""}`}
                              type="button"
                              onClick={() => setForm({ ...form, codigo_afip: String(o.code) })}
                              className={`w-full text-left px-2 py-1 hover:bg-emerald-50 flex items-center gap-2 border-t border-neutral-50 ${String(form.codigo_afip) === String(o.code) ? "bg-emerald-100" : ""}`}
                              data-testid={`prod-afip-opt-${o.code}`}
                            >
                              <span className="font-mono text-[11px] font-semibold text-emerald-800 w-14 text-right">{o.code}</span>
                              <span className="flex-1 truncate">{o.label}</span>
                              {o.grupo_label && (
                                <span className="text-[10px] text-neutral-400 truncate max-w-[100px]">{o.grupo_label}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                  {/* Fallback: input manual */}
                  <div className="border-t bg-neutral-50 px-2 py-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-neutral-500 uppercase">Manual:</span>
                      <input
                        type="text"
                        className="flex-1 text-xs font-mono outline-none bg-white px-2 py-1 rounded-sm border border-neutral-200"
                        placeholder="Escribir código libre"
                        value={form.codigo_afip}
                        onChange={(e) => setForm({ ...form, codigo_afip: e.target.value })}
                        data-testid="prod-codigo-afip"
                      />
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">
                  Nomenclatura AFIP para el <b>cod_tipo_prod</b> del Remito Cárnico. Elegí del listado o escribí manualmente.
                </div>
              </div>
              <div className="border border-[#E5E5E5] rounded-sm">
                <div className="px-3 py-2 bg-neutral-50 border-b border-[#E5E5E5] text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Costo del producto
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-[12px] flex-1 text-[#6B1F24] font-mono">Costo unitario</Label>
                    <NumberInput
                      data-testid="prod-ultimo-costo" step="0.01"
                      className="rounded-sm h-8 font-mono text-right w-32"
                      value={form.ultimo_costo ?? ""}
                      onChange={(e) => setForm({ ...form, ultimo_costo: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <RecetaCostoEditor form={form} setForm={setForm} productos={productos} productoId={editing?.id} />
                  <div className="text-[10px] text-[#666] font-mono pt-2">
                    Se actualiza automáticamente en cada compra si tildaste "Actualizar costo". También podés editarlo manualmente acá.
                  </div>
                </div>
              </div>
              <div className="border border-[#E5E5E5] rounded-sm">
                <div className="px-3 py-2 bg-neutral-50 border-b border-[#E5E5E5] text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Tienda Nube
                </div>
                <div className="p-3">
                  <label className="flex items-start gap-2 cursor-pointer" data-testid="prod-tn-flags">
                    <input
                      type="checkbox"
                      className="accent-[#6B1F24] mt-1"
                      checked={!!form.stock_infinito_tn}
                      onChange={(e) => setForm({ ...form, stock_infinito_tn: e.target.checked })}
                      data-testid="prod-stock-infinito-tn"
                    />
                    <div>
                      <div className="text-[12px] text-[#6B1F24] font-mono font-semibold">Stock infinito en Tienda Nube</div>
                      <div className="text-[10px] text-[#666] font-mono">
                        Al hacer "Sincronizar stock", se manda 9999 sin importar el stock real. Útil para productos que se hacen a demanda (ej. hamburguesas frescas).
                      </div>
                    </div>
                  </label>
                  <div className="mt-2 text-[10px] font-mono text-neutral-500 pt-2 border-t border-neutral-100">
                    El link con TN se hace por <strong>código de producto</strong> (SKU). Asegurate de tener el mismo código de este lado y en Tienda Nube.
                  </div>
                </div>
              </div>
              <div className="border border-[#E5E5E5] rounded-sm">
                <div className="px-3 py-2 bg-neutral-50 border-b border-[#E5E5E5] text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Precios por lista
                </div>
                <div className="p-3 space-y-2">
                  {listas.map((l) => {
                    const val = parseFloat(form.precios[l.id]);
                    const ivaPct = parseFloat(form.iva_pct) || 0;
                    const neto = !isNaN(val) && ivaPct > 0 ? val / (1 + ivaPct / 100) : (isNaN(val) ? null : val);
                    return (
                      <div key={l.id} className="flex items-center gap-2">
                        <Label className="text-[12px] flex-1 text-[#6B1F24] font-mono">{l.nombre}</Label>
                        <span className="text-[10px] font-mono text-neutral-500 w-24 text-right" title="Precio neto sin IVA (calculado)">
                          {neto != null ? `Neto ${fmtARS(neto)}` : ""}
                        </span>
                        <NumberInput
                          data-testid={`prod-precio-${l.id}`} step="0.01"
                          className="rounded-sm h-8 font-mono text-right w-32"
                          value={form.precios[l.id] ?? ""}
                          onChange={(e) => setForm({ ...form, precios: { ...form.precios, [l.id]: e.target.value } })}
                          placeholder="0.00"
                        />
                      </div>
                    );
                  })}
                  {listas.length === 0 && (
                    <div className="text-[12px] text-[#999]">
                      Todavía no hay listas de precios. Creá una desde la pestaña "Listas".
                    </div>
                  )}
                  <div className="text-[10px] text-[#666] font-mono pt-1 border-t border-neutral-100 mt-1">
                    Los precios se cargan c/IVA. El neto se calcula automáticamente con la alícuota de arriba.
                  </div>
                </div>
              </div>
              <Button
                data-testid="save-prod-btn" type="submit"
                className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
              >
                Guardar cambios
              </Button>
            </form>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete producto */}
      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar producto</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar <span className="font-mono text-[#6B1F24]">{delTarget?.codigo}</span> · {delTarget?.nombre}?
              Las ventas históricas no se ven afectadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nueva lista */}
      <Dialog open={newListaOpen} onOpenChange={setNewListaOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="new-lista-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nueva lista de precios</DialogTitle>
            <DialogDescription className="text-[11px]">
              Ej: Mostrador, Mayorista, Distribuidor, Especial.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus data-testid="new-lista-input" className="rounded-sm"
            placeholder="Nombre de la lista"
            value={newListaName}
            onChange={(e) => setNewListaName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createLista()}
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setNewListaOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-new-lista" onClick={createLista} className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete lista */}
      <AlertDialog open={!!delLista} onOpenChange={(v) => !v && setDelLista(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={16} className="text-amber-600" /> Eliminar lista
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar la lista <span className="font-mono">{delLista?.nombre}</span>? Los precios asignados a esta lista se perderán en cada producto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteLista} className="rounded-sm bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nuevo producto manual */}
      <Dialog open={newProdOpen} onOpenChange={setNewProdOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="new-prod-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo producto</DialogTitle>
            <DialogDescription className="text-[11px]">
              El código se autocompleta con el siguiente disponible. Podés editarlo si necesitás uno custom.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[12px]">Código</Label>
                <Input
                  data-testid="new-prod-codigo"
                  className="rounded-sm mt-1 font-mono text-[#6B1F24]"
                  value={newProd.codigo}
                  onChange={(e) => setNewProd({ ...newProd, codigo: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[12px]">Nombre</Label>
                <Input
                  data-testid="new-prod-nombre"
                  autoFocus
                  className="rounded-sm mt-1"
                  value={newProd.nombre}
                  onChange={(e) => setNewProd({ ...newProd, nombre: e.target.value })}
                  placeholder="Ej: Asado de tira"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Unidad</Label>
                <Input
                  data-testid="new-prod-unidad"
                  className="rounded-sm mt-1 font-mono"
                  value={newProd.unidad}
                  onChange={(e) => setNewProd({ ...newProd, unidad: e.target.value })}
                  placeholder="kg / u / lt"
                />
              </div>
              <div>
                <Label className="text-[12px]">Alícuota IVA</Label>
                <Select
                  value={String(newProd.iva_pct)}
                  onValueChange={(v) => setNewProd({ ...newProd, iva_pct: parseFloat(v) })}
                >
                  <SelectTrigger data-testid="new-prod-iva" className="rounded-sm mt-1 font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IVA_OPCIONES.map((o) => (
                      <SelectItem key={o.value} value={String(o.numeric)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[12px]">Rubro <span className="text-neutral-400 text-[10px]">(agrupación en POS)</span></Label>
              <Select
                value={newProd.rubro_id || "__none__"}
                onValueChange={(v) => setNewProd({ ...newProd, rubro_id: v === "__none__" ? null : v })}
              >
                <SelectTrigger data-testid="new-prod-rubro" className="rounded-sm mt-1">
                  <SelectValue placeholder="Seleccionar rubro..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin rubro —</SelectItem>
                  {rubros.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                        {r.nombre}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px]">Costo <span className="text-neutral-400 text-[10px]">unitario · editable · se actualiza con cada compra</span></Label>
              <NumberInput
                data-testid="new-prod-ultimo-costo" step="0.01"
                className="rounded-sm mt-1 font-mono text-right"
                value={newProd.ultimo_costo}
                onChange={(e) => setNewProd({ ...newProd, ultimo_costo: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Precio Minorista <span className="text-neutral-400 text-[10px]">c/IVA</span></Label>
                <NumberInput
                  data-testid="new-prod-precio-minorista" step="0.01"
                  className="rounded-sm mt-1 font-mono text-right"
                  value={newProd.precio_minorista}
                  onChange={(e) => setNewProd({ ...newProd, precio_minorista: e.target.value })}
                  placeholder="0.00"
                />
                {(() => {
                  const v = parseFloat(newProd.precio_minorista);
                  const p = parseFloat(newProd.iva_pct) || 0;
                  if (isNaN(v) || p <= 0) return null;
                  return <div className="text-[10px] text-neutral-500 font-mono mt-1 text-right">Neto {fmtARS(v / (1 + p / 100))}</div>;
                })()}
              </div>
              <div>
                <Label className="text-[12px]">Precio Mayorista <span className="text-neutral-400 text-[10px]">c/IVA</span></Label>
                <NumberInput
                  data-testid="new-prod-precio-mayorista" step="0.01"
                  className="rounded-sm mt-1 font-mono text-right"
                  value={newProd.precio_mayorista}
                  onChange={(e) => setNewProd({ ...newProd, precio_mayorista: e.target.value })}
                  placeholder="0.00"
                />
                {(() => {
                  const v = parseFloat(newProd.precio_mayorista);
                  const p = parseFloat(newProd.iva_pct) || 0;
                  if (isNaN(v) || p <= 0) return null;
                  return <div className="text-[10px] text-neutral-500 font-mono mt-1 text-right">Neto {fmtARS(v / (1 + p / 100))}</div>;
                })()}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setNewProdOpen(false)}>Cancelar</Button>
            <Button
              data-testid="submit-new-prod"
              onClick={createProdManual}
              disabled={creatingProd}
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
            >
              {creatingProd ? "Creando…" : "Crear producto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nuevo/Editar rubro */}
      <Dialog open={newRubroOpen} onOpenChange={(o) => { setNewRubroOpen(o); if (!o) { setEditRubroId(null); setRubroForm({ nombre: "", color: "#921A0E", orden: 0 }); } }}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editRubroId ? "Editar rubro" : "Nuevo rubro"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[12px]">Nombre *</Label>
              <Input
                data-testid="rubro-form-nombre"
                autoFocus
                className="rounded-sm mt-1"
                value={rubroForm.nombre}
                onChange={(e) => setRubroForm({ ...rubroForm, nombre: e.target.value })}
                placeholder="Ej: Vacuno"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    data-testid="rubro-form-color"
                    value={rubroForm.color}
                    onChange={(e) => setRubroForm({ ...rubroForm, color: e.target.value })}
                    className="w-10 h-9 rounded-sm border cursor-pointer"
                  />
                  <Input
                    className="rounded-sm font-mono text-[12px]"
                    value={rubroForm.color}
                    onChange={(e) => setRubroForm({ ...rubroForm, color: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-center mt-2">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-[11px] font-bold shadow" style={{ backgroundColor: rubroForm.color }}>
                {(rubroForm.nombre || "?").substring(0, 3).toUpperCase()}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setNewRubroOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-save-rubro" onClick={saveRubro} className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]">
              {editRubroId ? "Actualizar" : "Crear rubro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resultado import */}
      <Dialog open={!!importResult} onOpenChange={(v) => !v && setImportResult(null)}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Importación completa</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-[13px] font-mono">
            <div>Productos actualizados: <strong>{importResult?.productos_actualizados}</strong></div>
            <div>Productos nuevos: <strong>{importResult?.productos_creados}</strong></div>
            {importResult?.filas_ignoradas > 0 && (
              <div>Filas ignoradas (sin nombre): <strong>{importResult.filas_ignoradas}</strong></div>
            )}
            {importResult?.listas_creadas?.length > 0 && (
              <div>Listas creadas: <strong>{importResult.listas_creadas.join(", ")}</strong></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
